import * as http from '@/util/http'

export type AreaPrefectureMap = Record<string, string[]>
export type PrefectureAreaMap = Record<string, string>

export type AreaPrefectureMapping = {
  areaToPrefecture: AreaPrefectureMap
  prefectureToArea: PrefectureAreaMap
}

export async function fetchAreaPrefecturesMap(): Promise<AreaPrefectureMapping> {
  const { data: areaToPrefecture } = await http.get<AreaPrefectureMap>(
    '/ref/v1/geo/area-prefecture'
  )
  const prefectureToArea = Object.fromEntries(
    Object.entries(areaToPrefecture).flatMap(([area, prefectures]) =>
      prefectures.map((pref) => [pref, area] as const)
    )
  )
  return { areaToPrefecture, prefectureToArea }
}

export type Course = {
  id: number
  name: string
  isActive: boolean
}

export type Program = {
  id: number
  name: string
  programType: string
  durationYears: number
  isActive: boolean
  courses: Course[]
}

export type School = {
  id: number
  name: string
  institutionType: 'National' | 'Public' | 'Private'
  adminDivision: string
  subnationalUnit: string
  programs: Program[]
}

export async function fetchSchools(): Promise<School[]> {
  const { data } = await http.get<School[]>('/ref/v1/edu/school')
  return data
}

export type ProgramType = {
  id: string
}

export async function fetchProgramTypes(): Promise<ProgramType[]> {
  const { data } = await http.get<ProgramType[]>('/ref/v1/program-type')
  return data
}

export type Interest = {
  id: number
  name: string
  emoji?: string
}

export type InterestCategory = {
  id: number
  name: string
  emoji?: string
  description?: string
  reprId: number
  interests: Interest[]
}

export async function fetchInterests(): Promise<InterestCategory[]> {
  const { data } = await http.get<InterestCategory[]>('/ref/v1/interest')
  return data
}

export type Industry = {
  id: number
  name: string
}

export type IndustryCategory = {
  id: number
  name: string
  emoji?: string
  description?: string
  reprId: number
  industries: Industry[]
}

export async function fetchIndustries(): Promise<IndustryCategory[]> {
  const { data } = await http.get<IndustryCategory[]>('/ref/v1/industry')
  return data
}

export type Occupation = {
  id: number
  name: string
}

export type OccupationCategory = {
  id: number
  name: string
  emoji?: string
  description?: string
  reprId: number
  occupations: Occupation[]
}

export async function fetchOccupations(): Promise<OccupationCategory[]> {
  const { data } = await http.get<OccupationCategory[]>('/ref/v1/occupation')
  return data
}

export type Skill = {
  id: number
  name: string
}

export type SkillCategory = {
  id: number
  name: string
  interests: Skill[]
}

export async function fetchSkills(): Promise<SkillCategory[]> {
  const { data } = await http.get<SkillCategory[]>('/ref/v1/skill')
  return data
}

export type Qualification = {
  id: number
  name: string
}

export type QualificationCategory = {
  id: number
  name: string
  interests: Skill[]
}

export async function fetchQualifications(): Promise<QualificationCategory[]> {
  const { data } = await http.get<QualificationCategory[]>('/ref/v1/qualification')
  return data
}

export type Language = {
  id: number
  name: string
}

export async function fetchLanguages(): Promise<Language[]> {
  const { data } = await http.get<Language[]>('/ref/v1/language')
  return data
}
